import axios from 'axios';

/**
 * The AI coaching prompt offered to applicants on the /apply essay step.
 *
 * This replaces the former "I did not use Generative AI" attestation. The attestation
 * tried to keep AI out of the essay, which is both unenforceable and the wrong goal. The
 * prompt instead *directs* how AI is used: the applicant copies it into their own AI tool,
 * is interviewed by it, and brings their own answers back here. The prompt is written to
 * refuse to draft the essay, so what returns is the applicant's material rather than
 * synthesised filler.
 *
 * The markdown lives in the generic `admin_settings` key/value store, whose create and
 * update endpoints already write to the audit log — so the admin surface gets audit
 * coverage without its own logging path. Reads are available to any authenticated user,
 * which is what lets an applicant on /apply fetch the current prompt.
 */
export const ESSAY_AI_PROMPT_MARKDOWN_KEY = 'essay_ai_prompt_markdown';
export const ESSAY_AI_PROMPT_ENABLED_KEY = 'essay_ai_prompt_enabled';

/** Where "Open Copilot" sends the applicant after the prompt is on their clipboard. */
export const COPILOT_URL = 'https://copilot.microsoft.com/';

export const ESSAY_AI_PROMPT_DEFAULT = `# CLEAR Personal Statement — Interview Me

Help me think through my own personal statement for Comcast's CLEAR mentorship and
early-career development program. Do **not** write the essay for me.

The application asks: "In 500 words or fewer, tell us why you want to participate
in the CLEAR program. What do you hope to gain from the experience, and what will
you contribute to your peers?" I must review my response with my manager before
submitting. The question is deliberately broad: help me find my own reasons, not
fit me into a template.

First ask: "What role are you applying for — Mentee, Mentor, or Committee Member?"
Wait for my answer. Then ask: "How long should this statement be?" If I am unsure,
remind me the application asks for 500 words or fewer. Keep that limit in mind when
helping me choose which details matter; do not write or count a draft for me.

Ask one question at a time and wait for my answer. Tailor follow-ups to my role:
Mentees learn through sessions, project work and a triad; Mentors guide a mentee
within a triad; Committee Members support the mentor and mentee and connect the
triad to the committee. Do not assume what motivates me or imply I must have a
particular experience.

Explore why CLEAR appeals to me now, what I personally hope to gain, and what I
could contribute to my peers and triad. Ask for a real example or follow-up when
my answer is too generic, but let my answers determine the direction. Keep your
replies brief — one question and at most one short sentence of context, not a
long explanation or a list of questions.

Do not draft, rewrite, polish or ghostwrite the statement, not even an example
paragraph and not even if I ask you to. When I say I am done, return only a few
short bullet points with specific details I shared, using my own words, and at
most one brief note about what I might still think through. Then stop. I will
write and review my own statement.

Begin with the role question only.`;

export type EssayAiPrompt = {
  markdown: string;
  enabled: boolean;
};

type RawSetting = { setting_key: string; setting_value: string };

/**
 * Reads the prompt out of an already-fetched settings array. Kept separate from the fetch
 * so SiteSettings — which loads every setting in one request — needs no second round trip.
 */
export function readEssayAiPrompt(settings: RawSetting[]): EssayAiPrompt {
  const list = Array.isArray(settings) ? settings : [];
  const markdown = list.find(
    (s) => s.setting_key === ESSAY_AI_PROMPT_MARKDOWN_KEY
  )?.setting_value;
  const enabled = list.find(
    (s) => s.setting_key === ESSAY_AI_PROMPT_ENABLED_KEY
  )?.setting_value;

  return {
    // An empty string is a misconfiguration, not an intentional blank prompt — fall back
    // so the Copy button never puts nothing on the clipboard.
    markdown: markdown?.trim() ? markdown : ESSAY_AI_PROMPT_DEFAULT,
    // Unlike the attestation this replaced, an absent key means "show it". This is an
    // optional aid with a sensible built-in default, not a control whose absence would
    // weaken the application, so the safe default is on. Only an explicit 'false' hides it.
    enabled: enabled !== 'false',
  };
}

export async function fetchEssayAiPrompt(): Promise<EssayAiPrompt> {
  const response = await axios.get<RawSetting[]>('/api/adminSettings/settings');
  return readEssayAiPrompt(response.data ?? []);
}

/**
 * The settings API has no upsert: PUT 404s when the key has never been written. Every
 * caller in SiteSettings had hand-rolled the same try/catch, so it lives here once.
 */
export async function upsertSetting(params: {
  key: string;
  value: string;
  type: string;
  description: string;
  category: string;
}): Promise<void> {
  try {
    await axios.put(`/api/adminSettings/settings/${params.key}`, {
      setting_value: params.value,
    });
  } catch (error: any) {
    if (error?.response?.status !== 404) throw error;
    await axios.post('/api/adminSettings/settings', {
      setting_key: params.key,
      setting_value: params.value,
      setting_type: params.type,
      description: params.description,
      category: params.category,
    });
  }
}

/**
 * Copies text to the clipboard. The async Clipboard API requires a secure context, so it
 * is absent on plain-HTTP origins (which includes some local setups) — the caller needs
 * the boolean to decide whether to show a manual-copy fallback rather than claiming
 * success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (!navigator?.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
